import clsx from 'clsx';
import { isEmpty, lowerCase, pull } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useParams, useSearchParams } from 'react-router-dom';

import {
  Accordion,
  AccordionContent,
  AccordionProvider,
  AccordionTitle,
  Banner,
  BookingBloc,
  Breadcrumb,
  Gallery,
  HeroBanner,
  Icon,
  PrintTravelShowPage,
  ProductShowSkeleton,
  SanitizedHtml,
  SocialShare,
  TabPanel,
  Tabs,
  TabsProvider,
  TravelSummary
} from 'components';
import { capitalizeFirstLetter, isBreakpoint } from 'helpers';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

import travelHero from 'assets/img/travel-hero.png';
import 'assets/styles/components/products/product-show.scss';

const TravelShowPage = () => {
  const isDesktop = isBreakpoint(1024);

  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { currentUser } = useUser();

  const {
    call: tourOperatorCall,
    data: tourOperatorResponse,
    loading: tourOperatorLoading
  } = useFetch({ filterUserCompany: true });

  const { call: travelCall, data: travel, loading: travelLoading, error: errorTravel } = useFetch();
  const tourOperator = tourOperatorResponse?.data?.[0];
  const isTouloisirsTravel = travel?.tourOperator === 'Touloisirs';

  const heroData = useMemo(() => {
    if (isEmpty(tourOperator)) {
      return {
        heroTitle: t('yourResults'),
        heroImage: {
          frontUrl: travelHero
        }
      };
    } else {
      return tourOperator;
    }
  }, [tourOperatorResponse]);

  const breadcrumbs = useMemo(() => {
    let crumbs = [
      { url: routes.travelHomePage.path, label: t('breadcrumb.home') },
      { url: routes.results.path, label: t('breadcrumb.travels') }
    ];

    if (travel && !errorTravel) {
      const resultPath = routes.results.path;
      const searchParams = createSearchParams({
        tourOperatorId: travel.tourOperatorId ?? 'touloisirs',
        type: 'travels'
      }).toString();

      crumbs = [
        ...crumbs,
        { url: `${resultPath}?${searchParams}`, label: capitalizeFirstLetter(travel?.tourOperator) },
        { url: '', label: capitalizeFirstLetter(travel?.title) }
      ];
    }

    return crumbs;
  }, [travel, errorTravel]);

  const tabs = useMemo(() => {
    const detailTabs = [
      { identifier: 'hostingResume', label: t('products:tabs.hosting'), icon: { name: 'bed', style: 'fad' } },
      { identifier: 'cateringResume', label: t('products:tabs.catering'), icon: { name: 'utensils', style: 'fad' } },
      {
        identifier: 'servicesResume',
        label: t('products:tabs.services'),
        icon: { name: 'umbrella-beach', style: 'fad' }
      },
      { identifier: 'knowResume', label: t('products:tabs.know'), icon: { name: 'circle-heart', style: 'fad' } }
    ];

    const tarifTab = [
      { identifier: 'tariffResume', label: t('products:tabs.general') },
      { identifier: 'insuranceResume', label: t('products:tabs.insurance') },
      { identifier: 'formalityResume', label: t('products:tabs.formality') },
      { identifier: 'infoResume', label: t('products:tabs.info') }
    ];

    const removeEmptyTabs = (tabList) => {
      const list = tabList.map((tab) => {
        if (isEmpty(travel?.[tab.identifier])) return null;
        return tab;
      });

      return pull(list, null);
    };

    return { details: removeEmptyTabs(detailTabs), tarifs: removeEmptyTabs(tarifTab) };
  }, [travel]);

  useEffect(() => {
    getProduct();
  }, []);

  const getProduct = async () => {
    const searchParamsObj = Object.fromEntries(searchParams.entries());
    if (!id || !searchParamsObj?.typeItem || !currentUser?.serialNumber) return;

    const { data } =
      (await travelCall({
        service: 'castelis',
        method: 'get',
        path: `/product/travels/${currentUser.serialNumber}/travel/${id}/catalogue/${searchParamsObj.typeItem}/`
      })) || {};

    if (data?.body?.tourOperatorId || data?.body?.tourOperator) {
      const tourOperator = data?.body?.tourOperatorId || lowerCase(data?.body?.tourOperator);
      getTourOperatorContent(tourOperator);
    }
  };

  const getTourOperatorContent = (tourOperator) => {
    tourOperatorCall({
      service: appService.getStrapiServiceName(),
      method: 'get',
      path: '/api/result-pages',
      query: {
        filters: {
          page_type: { name: { $eq: 'travels' } },
          tour_operator_slug: { $eq: tourOperator }
        }
      }
    });
  };

  return (
    <>
      {travel && !errorTravel && <PrintTravelShowPage travel={travel} tabs={tabs} className="hidden print:block" />}

      <div className="product-show print:hidden">
        <div className="hidden lg:block">
          <HeroBanner
            title={heroData?.heroTitle || ''}
            image={heroData?.heroImageDesktop}
            mobileImage={heroData?.heroImageMobile}
            loading={tourOperatorLoading}
          />
        </div>

        <div className="main-container mt-20 lg:mb-4">
          <div className={clsx('lg:block', errorTravel ? 'mt-24' : 'hidden')}>
            <Breadcrumb breadcrumbItems={breadcrumbs} loading={travelLoading || tourOperatorLoading} />
          </div>

          {!travelLoading && errorTravel && (
            <Banner icon={{ name: 'circle-info', style: 'fad' }} type="warning" className="my-12">
              {t('contentUnavailable')}
            </Banner>
          )}
        </div>

        <Gallery images={travel?.visuals} loading={travelLoading} className="lg:hidden" />

        {travelLoading && <ProductShowSkeleton />}

        {!travelLoading && !errorTravel && !isEmpty(travel) && (
          <div className="product-container main-container">
            <div className="mb-8 lg:hidden">
              <Breadcrumb breadcrumbItems={breadcrumbs} loading={travelLoading || tourOperatorLoading} />
            </div>

            <div className="flex justify-between items-start mb-12">
              <h2 className={clsx('h2', { 'underlined-blue': isDesktop })}>{travel.title}</h2>

              <div className="hidden lg:flex gap-4">
                <SocialShare />

                <button type="button" onClick={() => window.print()}>
                  <Icon name="print" iconStyle="fad" classes="w-4 h-4 text-gray-500 lg:w-5 lg:h-5" />
                </button>
              </div>
            </div>

            <Gallery images={travel?.visuals} loading={travelLoading} className="hidden lg:block" />

            <div className="overflow-x-auto lg:pt-8 lg:gap-x-8 lg:flex 2xl:gap-x-16">
              <div className="w-full lg:w-[calc(100%-20rem)]">
                {isTouloisirsTravel ? (
                  <SanitizedHtml content={travel?.presentation} />
                ) : (
                  <SanitizedHtml content={travel?.resume} />
                )}

                {tabs.details.length > 0 && (
                  <div className="my-12">
                    <h2 className="h2 underlined-blue mb-10">{t('products:details')}</h2>

                    <div className="lg:hidden">
                      <AccordionProvider defaultOpen={[0]} allowMultipleOpen>
                        {tabs.details.map((tab, i) => (
                          <Accordion key={`accordion-single-${i}`} itemIndex={i}>
                            <AccordionTitle icon={{ name: 'angle-down', class: 'accordion-arrow' }}>
                              <div className="flex items-center gap-6">
                                <Icon
                                  name={tab.icon.name}
                                  iconStyle={tab.icon.style}
                                  classes="w-6 h-6 text-primary-500"
                                />
                                <span>{tab.label}</span>
                              </div>
                            </AccordionTitle>

                            <AccordionContent>
                              <SanitizedHtml content={travel[tab.identifier]} />
                            </AccordionContent>
                          </Accordion>
                        ))}
                      </AccordionProvider>
                    </div>

                    <div className="hidden lg:block">
                      <TabsProvider defaultTab={tabs.details[0].identifier}>
                        <Tabs tabs={tabs.details} className="detail-tabs" />

                        {tabs.details.map((tab, i) => (
                          <TabPanel identifier={tab.identifier} key={`detail-tab-${i}`}>
                            <SanitizedHtml content={travel[tab.identifier]} className="pt-8" />
                          </TabPanel>
                        ))}
                      </TabsProvider>
                    </div>
                  </div>
                )}

                {tabs.tarifs.length > 0 && (
                  <>
                    <h2 className="h2 underlined-blue mb-10">{t('products:tarif')}</h2>

                    <TabsProvider defaultTab={tabs.tarifs[0].identifier}>
                      <Tabs tabs={tabs.tarifs} className="tarif-tabs hidden-scrollbar" />

                      {tabs.tarifs.map((tab, i) => (
                        <TabPanel identifier={tab.identifier} key={`tarif-tab-${i}`}>
                          <SanitizedHtml content={travel[tab.identifier]} className="py-4 lg:py-8" />
                        </TabPanel>
                      ))}
                    </TabsProvider>
                  </>
                )}
              </div>

              <div className="booking-container">
                <TravelSummary travel={travel} />

                <BookingBloc blocks={tourOperator?.reservationBlocs} bookingBlockLoading={tourOperatorLoading} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TravelShowPage;
